// Third-party
import React from 'react';
import { withFormik, Form, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';

// Components
import ModalV2 from '@/views/components/modals/ModalV2';
import { UnitInput } from '@/views/components/inputs';

// Utils
import { formatMessage } from '@/utils/i18n';
import { CURRENCY } from '@/utils/currency';

// Types
import User from '@/types/user';
import Nullable from '@/types/nullable';
import CustomYieldParams from '@/types/custom-yield-params';
import ShelvableInstrument from '@/types/shelvable-instrument';
import Shelf from '@/types/shelf';
import CompliantInstrument from '@/types/compliant-instrument';
import PortfolioHolding from '@/types/portfolio-holding';
import ProxySimulationOptionsType from '@/types/proxy-simulation-options-type';

// Redux
import { ReduxState } from '@/redux/modules/types';
import { addCustomYieldHolding } from '@/redux/modules/edit-portfolio';

// Define interfaces
interface FormValues {
  rate: string;
}

interface RateOfReturnModalProps {
  show?: boolean;
  proxyName?: string;
  options: ProxySimulationOptionsType;
  holdingToReplace?: PortfolioHolding;
  filteredShelves?: Shelf[];
  onConfirm?: (rate: number) => void;
  onClosed?: () => void;
}

type DispatchProps = {
  addCustomYieldHolding: (
    config: CustomYieldParams,
    shelfInfo: ShelvableInstrument & CompliantInstrument,
    currency: string,
    holdingToReplace?: PortfolioHolding,
    showReplacedFirst?: boolean,
  ) => void;
};

type StateProps = {
  user?: Nullable<User>;
};

type CombinedProps = RateOfReturnModalProps & DispatchProps & StateProps;

interface InputProps {
  name: string;
  type: 'text' | 'number' | 'password' | 'email' | 'search';
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
}

interface FormikFieldProps {
  field: {
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  };
  form: FormikProps<FormValues>;
  type: 'text' | 'number' | 'password' | 'email' | 'search';
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
}

type Props = FormikProps<FormValues> & CombinedProps;

const FormPercentInput = ({ field, form, ...props }: FormikFieldProps) => {
  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    // Convert FormEvent to ChangeEvent for Formik compatibility
    const changeEvent = event as React.ChangeEvent<HTMLInputElement>;
    field.onChange(changeEvent);
  };

  const handleBlur = (event?: React.FocusEvent<HTMLInputElement>) => {
    if (event) {
      field.onBlur(event);
    }
  };

  const handleValueChange = (value: string) => {
    form.setFieldValue(field.name, value);
    form.setFieldTouched(field.name, true);
  };

  return (
    <UnitInput
      {...props}
      name={field.name}
      value={field.value}
      unitSymbol="%"
      className="percent-input unit-input-v2"
      onChange={handleChange}
      onBlur={handleBlur}
      onValueChange={handleValueChange}
    />
  );
};

const FormPercentInputField = (props: InputProps) => <Field {...props} component={FormPercentInput} />;

const canSubmit = ({ isSubmitting, errors }: { isSubmitting: boolean; errors: Record<string, string> }) => {
  const hasErrors = Object.keys(errors).length > 0;

  // Not checking isDirty - the default value is valid for submission
  return !hasErrors && !isSubmitting;
};

const RateOfReturnModal = (props: Props) => {
  const { show, proxyName, options, onClosed, isSubmitting } = props;

  // Handler to close modal - will display the X button in header
  const handleClose = () => {
    if (onClosed) {
      onClosed();
    }
  };

  const body = (
    <Form>
      <div className="rate-of-return-modal">
        <div className="rate-section">
          <div className="rate-title">
            <strong>{formatMessage('Modal.RateOfReturn.Title')}</strong>
          </div>
          <div className="desired-rate-label">
            <small>{formatMessage('Modal.RateOfReturn.DesiredRateLabel')}</small>
          </div>
          <div className="input-container">
            <FormPercentInputField name="rate" type="number" step={0.01} min={options.minimumReturn} max={options.maximumReturn} placeholder="0.00" />
          </div>
          <div className="range-label">
            <small>{formatMessage('Modal.RateOfReturn.RangeLabel', { start: options.minimumReturn.toString(), end: options.maximumReturn.toString() })}</small>
          </div>
          {props.errors.rate && props.touched.rate && (
            <div className="field-error-label">
              <i className="icon-exclamation-outline" />
              <span>{props.errors.rate}</span>
            </div>
          )}
        </div>
      </div>
    </Form>
  );

  return (
    <ModalV2
      show={show}
      title={proxyName}
      body={body}
      onClose={handleClose}
      modalClassName="rate-of-return-modal-wrapper"
      backdropClassName="modal-v2-backdrop"
      size="sm"
      defaultFooterButtons={{
        primaryLabelToLocalize: 'Modal.RateOfReturn.AddToPortfolio',
        primaryOnClick: () => {
          props.handleSubmit();
        },
        primaryDisabled: !canSubmit(props),
        primaryActionLoading: isSubmitting,
      }}
    />
  );
};

// Create a dynamic validation schema based on props
const createValidationSchema = (start: number, end: number) =>
  Yup.object().shape({
    rate: Yup.string()
      .required('Rate is required')
      .test('is-number', 'Rate must be a number', value => !isNaN(Number(value)))
      .test('min', `Rate must be at least ${start}%`, value => Number(value) >= start)
      .test('max', `Rate cannot exceed ${end}%`, value => Number(value) <= end),
  });

const formikWrapper = withFormik<CombinedProps, FormValues>({
  validationSchema: (props: CombinedProps) => createValidationSchema(props.options.minimumReturn, props.options.maximumReturn),
  mapPropsToValues: props => ({
    rate: props.options.defaultReturn.toString(),
  }),
  validateOnChange: true,
  validateOnBlur: true,
  handleSubmit: ({ rate }, { props, setSubmitting }) => {
    const { user, holdingToReplace } = props;
    const currency: string = user?.appConfig.localCurrency || CURRENCY.CAD;
    const rateNum = Number(rate);
    const { startDate, term, termUnits, frequency, proxySection } = props.options;

    // Create the config similar to HISA simulation
    const config: CustomYieldParams = {
      automaticRenewal: true,
      benchmarkLipperId: undefined,
      frequency,
      name: props.proxyName ?? 'Simulation',
      rate: rateNum,
      startDate,
      term,
      termUnits,
      type: proxySection,
    };

    const shelfInfo: ShelvableInstrument & CompliantInstrument = {
      filteredShelves: props.filteredShelves || [],
      complianceComparisons: [],
    };

    // Add the custom yield holding to portfolio
    props.addCustomYieldHolding(config, shelfInfo, currency, holdingToReplace);

    if (props.onClosed) {
      props.onClosed();
    }
    setSubmitting(false);
  },
});

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user,
});

const mapDispatchToProps: DispatchProps = {
  addCustomYieldHolding,
};

const ConnectedRateOfReturnModal = connect(mapStateToProps, mapDispatchToProps)(formikWrapper(RateOfReturnModal));

export default ConnectedRateOfReturnModal;
